<template>
  <Section
    :id="id"
    :header="t('documents.documents', 2)"
    :loading="loading || documents.loading || requiredDocumentTypesLoading"
    class="carrier-portal-document-section"
    data-class="documents-container flex flex-gap-1 flex-column"
  >
    <div v-if="!initialLoading" class="document-list">
      <Message
        v-if="showMissingRequiredBanner"
        severity="error"
        :closable="false"
        class="missing-required-banner"
      >
        <div class="missing-required-banner-headline">
          {{
            t('carrierPortal.documents.missingRequiredDocs', {
              count: missingRequiredCount,
            })
          }}
        </div>
        <div class="missing-required-banner-hint">
          {{ t('carrierPortal.documents.uploadMissingDocsHint') }}
        </div>
      </Message>

      <ul role="list" class="doc-sublist">
        <li v-for="type in placeholderDocumentTypes" :key="type">
          <CarrierPortalDocumentListItem
            :doc="defaultDocuments[type]?.data"
            :type="type"
            :error="defaultDocuments[type]?.error"
            :loading="
              loading || documents.loading || defaultDocuments[type]?.loading
            "
            :required="hasRequiredDocs && !!documents.data"
            @remove="onRemove"
            @retry="onRetry"
            @upload="onAttemptUpload"
          />
        </li>
      </ul>

      <template v-if="nonDefaultDocuments.length">
        <h3 class="section-subheader">
          {{ t('common.otherParam', { param: t('documents.documents', 2) }) }}
        </h3>
        <ul role="list" class="doc-sublist">
          <li v-for="doc in nonDefaultDocuments" :key="doc.id">
            <CarrierPortalDocumentListItem
              :doc="doc"
              :type="doc.types[0]"
              :error="doc.id ? docErrorsById[doc.id] : undefined"
              :loading="loading || documents.loading"
              @remove="onRemove"
              @retry="onRetry"
              @upload="onAttemptUpload"
            />
          </li>
        </ul>
      </template>
    </div>

    <DocumentUploadModal
      v-model:visible="uploadModal.visible"
      :doc-type="uploadModal.type"
      document-types-required
      @upload="onUpload"
    />
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import { CorsairDocumentType, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  DocumentProperties,
  DocumentReference,
  DocumentUploadPayload,
  SignedDocument,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { getContentTypeFor } from '@/util';

import CarrierPortalDocumentListItem from '@/components/carrierPortal/document/CarrierPortalDocumentListItem.vue';
import DocumentUploadModal from '@/components/document/DocumentUploadModal.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Message from 'primevue/message';

const DEFAULT_DOCUMENT_TYPES: CorsairDocumentType[] = [
  CorsairDocumentType.BOL,
  CorsairDocumentType.INVOICE,
  CorsairDocumentType.PROOF_OF_DELIVERY,
];

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipmentId: {
    type: Number,
    default: () => -1,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  additionalRequiredDocumentTypes: {
    type: Array as PropType<CorsairDocumentType[]>,
    default: () => [],
  },
  sentFromRequiredDocumentsEmail: {
    type: Boolean,
    default: false,
  },
  requiredDocumentTypesLoading: {
    type: Boolean,
    default: false,
  },
});
const {
  id,
  shipmentId,
  loading,
  additionalRequiredDocumentTypes,
  sentFromRequiredDocumentsEmail,
  requiredDocumentTypesLoading,
} = toRefs(props);

const { isAuthenticatedUser } = useCurrentUserStore();
const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const documents: Ref<Loadable<SignedDocument[]>> = ref({ loading: false });

const defaultDocuments: Ref<
  Partial<Record<CorsairDocumentType, Loadable<SignedDocument>>>
> = ref(
  Object.fromEntries(
    DEFAULT_DOCUMENT_TYPES.map((t) => [t, { loading: false }]),
  ),
);

const initialLoading = computed(
  () =>
    requiredDocumentTypesLoading.value ||
    (documents.value.loading && !documents.value.data),
);

const hasRequiredDocs = computed(
  () => additionalRequiredDocumentTypes.value.length > 0,
);

const placeholderDocumentTypes = computed<CorsairDocumentType[]>(() => {
  return hasRequiredDocs.value
    ? [...new Set(additionalRequiredDocumentTypes.value)]
    : DEFAULT_DOCUMENT_TYPES;
});

const missingRequiredCount = computed(() => {
  if (!hasRequiredDocs.value || !documents.value.data) {
    return 0;
  }
  return placeholderDocumentTypes.value.filter(
    (type) => !defaultDocuments.value[type]?.data,
  ).length;
});

const showMissingRequiredBanner = computed(
  () => missingRequiredCount.value > 0 && sentFromRequiredDocumentsEmail.value,
);

watch(
  additionalRequiredDocumentTypes,
  (types) => {
    types.forEach((type) => {
      if (!defaultDocuments.value[type]) {
        defaultDocuments.value[type] = { loading: false };
      }
      const existing = documents.value.data?.find((d) =>
        d.types.includes(type),
      );
      if (existing && !defaultDocuments.value[type].data) {
        defaultDocuments.value[type].data = existing;
      }
    });
  },
  { immediate: true },
);

/** Errors for documents that don't have a default document type */
const docErrorsById: Ref<Record<number, unknown>> = ref({});

const uploadModal = ref({
  visible: false,
  type: undefined as CorsairDocumentType | undefined,
});

const accessToken: Ref<string | undefined> = computed(() => {
  return router.currentRoute.value.query.accessToken as string | undefined;
});

const nonDefaultDocuments = computed(() => {
  return (
    documents.value.data?.filter((d) => {
      const hasDefaultType = d.types.some((t) =>
        placeholderDocumentTypes.value.includes(t),
      );
      return !hasDefaultType;
    }) ?? []
  );
});

const loadDocuments = async (accessToken: string) => {
  try {
    documents.value.loading = true;
    setLoadingForAllDefaultDocs(true);
    documents.value.data = isAuthenticatedUser.value
      ? await api.shipmentDocument.getDocumentsByShipmentId(shipmentId.value)
      : await api.carrierPortal.getDocumentsByShipmentId(
          shipmentId.value,
          accessToken,
        );

    // Split out the default documents into their own state
    const docs = documents.value.data;
    if (!docs) {
      return;
    }

    placeholderDocumentTypes.value.map((defaultType) => {
      const doc = docs.find((d) => d.types.includes(defaultType));
      getDefaultDocument(defaultType).data = doc;
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading documents', e);
    documents.value.error = e;
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('documents.documents', 2),
      }),
      error: e,
    });
  } finally {
    documents.value.loading = false;
    setLoadingForAllDefaultDocs(false);
  }
};

const onRemove = async (doc: SignedDocument) => {
  if (!doc.id || !accessToken.value) {
    return;
  }

  const defaultTypesHoldingDoc = doc.types.filter((t) => {
    if (!placeholderDocumentTypes.value.includes(t)) {
      return false;
    }
    return defaultDocuments.value[t]?.data?.id === doc.id;
  });

  try {
    if (defaultTypesHoldingDoc.length) {
      defaultTypesHoldingDoc.forEach((type) => {
        getDefaultDocument(type).loading = true;
      });
    } else {
      documents.value.loading = true;
    }
    isAuthenticatedUser.value
      ? await api.shipmentDocument.removeDocument(shipmentId.value, doc.id)
      : await api.carrierPortal.removeDocument(
          shipmentId.value,
          doc.id,
          accessToken.value,
        );

    const index = documents.value.data?.findIndex((d) => d.id === doc.id) ?? -1;
    if (index !== -1) {
      documents.value.data?.splice(index, 1);
    }

    if (defaultTypesHoldingDoc.length) {
      defaultTypesHoldingDoc.forEach((type) => {
        const defaultDoc = getDefaultDocument(type);
        defaultDoc.data = undefined;
        defaultDoc.error = undefined;
      });
    } else {
      docErrorsById.value[doc.id] = undefined;
    }

    toast.addSuccess({
      detail: t('messages.successDeletingParam', {
        param: t('documents.documents'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error removing document', e);
    toast.addError({
      detail: t('errors.deletingParam', {
        param: t('documents.documents'),
      }),
      error: e,
    });

    if (defaultTypesHoldingDoc.length) {
      defaultTypesHoldingDoc.forEach((type) => {
        getDefaultDocument(type).error = e;
      });
    } else {
      docErrorsById.value[doc.id] = e;
    }
  } finally {
    if (defaultTypesHoldingDoc.length) {
      defaultTypesHoldingDoc.forEach((type) => {
        getDefaultDocument(type).loading = false;
      });
    } else {
      documents.value.loading = false;
    }
  }
};

const onRetry = async (type: CorsairDocumentType) => {
  const defaultDoc = getDefaultDocument(type);
  const payload = defaultDoc.error as DocumentUploadPayload;
  onUpload(payload);
};

const onAttemptUpload = async (type?: CorsairDocumentType) => {
  uploadModal.value.type = type;
  uploadModal.value.visible = true;
};

const onUpload = async ({ file, properties }: DocumentUploadPayload) => {
  if (!accessToken.value) {
    return;
  }

  let matchingTypes: CorsairDocumentType[];
  if (uploadModal.value.type) {
    matchingTypes = [uploadModal.value.type];
  } else {
    matchingTypes = properties.documentTypes.filter((t) =>
      placeholderDocumentTypes.value.includes(t),
    );
  }

  const defaultDocs = matchingTypes
    .map((t) => defaultDocuments.value[t])
    .filter((d): d is Loadable<SignedDocument> => d !== undefined);

  try {
    if (defaultDocs.length) {
      defaultDocs.forEach((d) => {
        d.loading = true;
      });
    } else {
      documents.value.loading = true;
    }

    const docRef = DocumentReference.fromFileAndEntityId(
      file,
      shipmentId.value,
    );
    docRef.entityType = 'SHIPMENT';
    const { fileName, uploadUrl } = isAuthenticatedUser.value
      ? await api.shipmentDocument.getUploadUrl(shipmentId.value, docRef)
      : await api.carrierPortal.getUploadUrl(
          shipmentId.value,
          docRef,
          accessToken.value,
        );

    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': getContentTypeFor(file),
      },
    });

    const documentProperties = DocumentProperties.fromFile(file, {
      ...properties,
      fileName,
    });
    const uploadedDocument = isAuthenticatedUser.value
      ? await api.shipmentDocument.attachDocument(
          shipmentId.value,
          documentProperties,
        )
      : await api.carrierPortal.attachDocument(
          shipmentId.value,
          documentProperties,
          accessToken.value,
        );

    if (defaultDocs.length) {
      defaultDocs.forEach((d) => {
        d.data = uploadedDocument;
        d.error = undefined;
      });
    } else {
      documents.value.data?.push(uploadedDocument);
    }

    toast.addSuccess({
      detail: t('messages.successUploadingParam', {
        param: t('documents.documents'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error uploading document', e);
    if (defaultDocs.length) {
      defaultDocs.forEach((d) => {
        d.error = { file, properties };
      });
    }
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('documents.documents'),
      }),
      error: e,
    });
  } finally {
    if (defaultDocs.length) {
      defaultDocs.forEach((d) => {
        d.loading = false;
      });
    } else {
      documents.value.loading = false;
    }
  }
};

const getDefaultDocument = (
  defaultType: CorsairDocumentType,
): Loadable<SignedDocument> => {
  const docState = defaultDocuments.value[defaultType];
  if (!docState) {
    throw new Error(`Can't find state for type ${defaultType}`);
  }

  return docState;
};

const setLoadingForAllDefaultDocs = (loading: boolean) => {
  placeholderDocumentTypes.value.forEach((defaultType) => {
    if (defaultDocuments.value[defaultType]) {
      defaultDocuments.value[defaultType].loading = loading;
    }
  });
};

onMounted(() => {
  if (accessToken.value) {
    loadDocuments(accessToken.value);
  }
});

defineExpose({
  showModal: onAttemptUpload,
});
</script>

<style>
.carrier-portal-document-section {
  .document-list {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }

  .missing-required-banner {
    .missing-required-banner-headline {
      font-weight: 600;
      line-height: 1.3;
    }

    .missing-required-banner-hint {
      color: var(--p-gray-600);
      font-size: 0.95rem;
    }
  }

  .doc-sublist {
    list-style: none;
    margin: 0;
    padding: 0;

    > li {
      border-top: 1px solid var(--p-gray-200);

      &:last-child {
        border-bottom: 1px solid var(--p-gray-200);
      }
    }
  }
}
</style>
